import {
  endOfMonth,
  format,
  isAfter,
  isEqual,
  isThisMonth,
  isValid,
  lastDayOfMonth,
  parseISO,
  startOfMonth,
  subMonths
} from 'date-fns'

export type StepStatus = 'idle' | 'running' | 'success' | 'warning' | 'error'

export type ShipStatusResponse = {
  maxStatementDate: string | null
  suggestedStatementEndDate: string | null
  appEndDate: string | null
  lastShipDate: string | null
}

const DATE_PART_REGEX = /^\d{4}-\d{2}-\d{2}$/

export const formatDisplayDate = (dateValue: string | null | undefined): string => {
  if (!dateValue) {
    return 'N/A'
  }

  const datePart = dateValue.slice(0, 10)
  if (!DATE_PART_REGEX.test(datePart)) {
    return dateValue
  }

  const parsedDate = parseISO(datePart)
  if (!isValid(parsedDate)) {
    return dateValue
  }

  return format(parsedDate, 'MM/dd/yyyy')
}

export const isInCurrentMonth = (dateValue: string | null | undefined): boolean => {
  if (!dateValue) {
    return false
  }

  const datePart = dateValue.slice(0, 10)
  if (!DATE_PART_REGEX.test(datePart)) {
    return false
  }

  const parsedDate = parseISO(datePart)
  return isValid(parsedDate) ? isThisMonth(parsedDate) : false
}

export const isSameMonthOrAfter = (
  dateValue: string | null | undefined,
  referenceDateValue: string | null | undefined
): boolean => {
  if (!dateValue || !referenceDateValue) {
    return false
  }

  const datePart = dateValue.slice(0, 10)
  const referenceDatePart = referenceDateValue.slice(0, 10)
  if (!DATE_PART_REGEX.test(datePart) || !DATE_PART_REGEX.test(referenceDatePart)) {
    return false
  }

  const parsedDate = parseISO(datePart)
  const parsedReferenceDate = parseISO(referenceDatePart)

  if (!isValid(parsedDate) || !isValid(parsedReferenceDate)) {
    return false
  }

  const dateMonth = startOfMonth(parsedDate)
  const referenceMonth = startOfMonth(parsedReferenceDate)

  return isAfter(dateMonth, referenceMonth) || isEqual(dateMonth, referenceMonth)
}

export const isLastDayOfMonth = (dateValue: string | null | undefined): boolean => {
  if (!dateValue) {
    return false
  }

  const datePart = dateValue.slice(0, 10)
  if (!DATE_PART_REGEX.test(datePart)) {
    return false
  }

  const parsedDate = parseISO(datePart)
  if (!isValid(parsedDate)) {
    return false
  }

  return isEqual(parsedDate, lastDayOfMonth(parsedDate))
}

export const getDefaultStatementEndDate = (): string => {
  return format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd')
}

export const getErrorMessage = (error: any): string => {
  if (!error) {
    return 'Unknown error'
  }

  if (typeof error === 'string') {
    return error
  }

  if (typeof error?.message === 'string') {
    return error.message
  }

  return JSON.stringify(error)
}
